// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';
import { onboardingApi } from '@shared/data-access/api/engine/onboarding-api';
import { useModal, useStepsData } from '@shared/ui-kit/hooks';
import { createDataProvider } from '@shared/ui-kit/providers';
import produce from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AccountWorkflowBlock,
  AccountWorkflowFormData,
  AccountWorkflowProviderProps,
  AccountWorkflowSection,
  AccountWorkflowKind,
  CombinedSteps,
  EmployedProfileFormData,
} from '../types';
import { EmploymentStatus } from '../types';
import {
  getAccountOpeningBlocks,
  getCombinedProfilingBlocks,
} from '../layout/progress-tracker/combined-flow-steps';
import {
  buildAccountOpeningFormData,
  buildAccountOpeningPayload,
  buildCitecClientDataFromPersonalData,
  buildOnboardingFormData,
  buildOnboardingQA,
  hasOnboardingSavedData,
  hydratePersonalDataFromCitecClientProfile,
} from '../utils';
import type { PersonalDataFormData } from '../types';

const getClientProfilePayload = (personalData: PersonalDataFormData) =>
  buildCitecClientDataFromPersonalData(personalData);

export type SavePersonalDataOptions = {
  updateRemoteProfile?: boolean;
};

export type SaveAdvisoryContractDataOptions = {
  calculateTestResults?: boolean;
};

export type SaveAccountOpeningDataOptions = {
  submitToInversis?: boolean;
  updatedFormData?: Partial<AccountWorkflowFormData>;
};

const getInitialBlocks = (
  workflow: AccountWorkflowKind,
): AccountWorkflowBlock[] => {
  if (workflow === 'profiling') {
    return [
      {
        id: 'contract-review',
        label: ['onboarding-blocks.contract-review', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'personal-data',
        label: ['onboarding-blocks.personal-data', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'investment-objectives',
        label: ['onboarding-blocks.investment-objectives', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'financial-knowledge',
        label: ['onboarding-blocks.financial-knowledge', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'investment-experience',
        label: ['onboarding-blocks.investment-experience', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'financial-situation',
        label: ['onboarding-blocks.financial-situation', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'sustainability',
        label: ['onboarding-blocks.sustainability', { ns: 'components' }],
        completed: false,
      },
      {
        id: 'result-and-signature',
        label: ['onboarding-blocks.result-and-signature', { ns: 'components' }],
        completed: false,
      },
    ];
  }

  if (workflow === 'combined') {
    return getAccountOpeningBlocks();
  }

  return getAccountOpeningBlocks({ includeNextSteps: true });
};

const initialFormData: AccountWorkflowFormData = {
  personalData: {
    first_name: '',
    second_name: '',
    first_surname: '',
    second_surname: '',
    birth_date: '',
    marital_status: 'single',
    gender: 'male',
    birth_city: '',
    nationality: 'spain',
    document_type: 'dni-nif',
    document_number: '',
    document_expiration_date: '',
    document_permanent_validity: false,
    email: '',
    phone_number: '',
    postal_address: {
      street_type: '',
      street_name: '',
      street_number: '',
      additional_address_info: '',
      province: '',
      postal_code: '',
      city: '',
      country: 'spain',
    },
    has_different_fiscal_address: false,
    fiscal_address: undefined,
  },
  professionalProfile: {
    employmentStatus: EmploymentStatus.Employed,
    companyName: '',
    sector: '',
    companyActivity: '',
  } satisfies EmployedProfileFormData,
  wealthSourceFunds: {
    mainIncomeSource: 'income-salary-pension',
    estimatedAnnualIncome: 'annual-50k-100k',
    approximateWealth: 'between-100k-300k',
    sourceOfFunds: ['funds-salary'],
  },
  accountUsage: {
    accountPurpose: ['savings', 'investment'],
    monthlyOperations: 'ops-tier-low',
    monthlyTransfers: 'transfers-tier-low',
    accountCheques: 'cheques-no',
  },
  legalDeclarations: {
    taxResidenceCountry: 'spain',
    taxResidenceDocumentNumber: '',
    investmentOwnership: 'self',
    isPoliticallyExposedPerson: false,
    isRelatedToPublicPerson: false,
    isUsTaxResidence: false,
  },
  signature: {
    acceptedAccountTerms: false,
    acceptedDataProtection: false,
    confirmedInformationTruthfulness: false,
  },
  investmentObjectives: undefined,
  financialKnowledge: undefined,
  investmentExperience: undefined,
  financialSituation: undefined,
  sustainability: undefined,
  resultAndSignature: undefined,
};

export const useAccountWorkflowFactory = (props: AccountWorkflowProviderProps) => {
  const { workflow, proposal } = props;
  const hasExistingInversisAccount =
    props.workflow === 'combined'
      ? props.hasExistingInversisAccount ?? false
      : false;
  const { t } = useTranslation();
  const { reset } = useStepsData();

  const { data: profileData, ...clientProfileQuery } =
    advisoryApi.useGetCitecUserClientProfileQuery();

  const { data: onboardingState, ...onboardingStateQuery } =
    onboardingApi.useGetClientOnboardingStateQuery();

  const [
    updateClientOnboardingProfile,
    { isLoading: isUpdatingClientOnboardingProfile },
  ] = onboardingApi.useUpdateClientOnboardingProfileMutation();

  const [
    updateCitecUserClientProfile,
    { isLoading: isUpdatingCitecUserClientProfile },
  ] = advisoryApi.useUpdateCitecUserClientProfileMutation();

  const [
    updateAdvisoryContract,
    { isLoading: isUpdatingAdvisoryContract, data: suitabilityTestResults },
  ] = advisoryApi.useUpdateAdvisoryContractMutation();

  const [signAdvisoryContractMutation, { isLoading: isSigningAdvisoryContract }] =
    advisoryApi.useSignAdvisoryContractMutation();

  const [formData, setFormData] =
    useState<AccountWorkflowFormData>(initialFormData);
  const [blocks, setBlocks] = useState(() => getInitialBlocks(workflow));
  const [navigationFloorStepId, setNavigationFloorStepId] = useState<
    CombinedSteps | undefined
  >();
  const [verificationCompleted, setVerificationCompleted] = useState(false);
  const skipProfileHydrationRef = useRef(false);
  const skipOnboardingHydrationRef = useRef(false);

  const setNavigationFloor = useCallback((stepId: CombinedSteps) => {
    setNavigationFloorStepId(stepId);
  }, []);

  const markStepAsCompleted = useCallback((step: AccountWorkflowSection) => {
    setBlocks(prev =>
      produce(prev, draft => {
        const block = draft.find(item => item.id === step);

        if (block) {
          block.completed = true;
        }
      }),
    );
  }, []);

  /** True when the block is completed and the step form has no pending edits. */
  const isStepCompleted = useCallback(
    (step: AccountWorkflowSection, isDirty: boolean): boolean =>
      !isDirty && (blocks.find(block => block.id === step)?.completed ?? false),
    [blocks],
  );

  const switchToProfilingBlocks = useCallback(() => {
    if (workflow !== 'combined') {
      return;
    }

    setBlocks(getCombinedProfilingBlocks());
  }, [workflow]);

  const skipAccountOpeningPhase = useCallback(() => {
    switchToProfilingBlocks();
  }, [switchToProfilingBlocks]);

  const hasCompletedProfiling = useMemo(
    () =>
      workflow === 'combined' &&
      (proposal?.contract?.status === 'active' ||
        proposal?.contract?.status === 'sent'),
    [proposal?.contract?.status, workflow],
  );

  const isAwaitingContractSignature = useMemo(
    () => workflow === 'combined' && proposal?.contract?.status === 'sent',
    [proposal?.contract?.status, workflow],
  );

  const hasPartialProfilingData = useMemo(
    () =>
      workflow === 'combined' &&
      proposal?.contract !== null &&
      proposal.contract.status !== 'active' &&
      Object.keys(proposal.contract.onboarding_qa ?? {}).length > 0,
    [proposal?.contract, workflow],
  );

  const canResumeToProfilingResults = useMemo(
    () =>
      hasPartialProfilingData &&
      proposal?.contract?.risk_profile !== null &&
      !isAwaitingContractSignature,
    [
      hasPartialProfilingData,
      isAwaitingContractSignature,
      proposal?.contract?.risk_profile,
    ],
  );

  const resumeToProfilingResults = useCallback(() => {
    if (workflow !== 'combined') {
      return;
    }

    setBlocks(
      produce(getCombinedProfilingBlocks(), draft => {
        draft.forEach(block => {
          block.completed = true;
        });
      }),
    );
  }, [workflow]);

  const saveFormData = useCallback(
    <K extends keyof AccountWorkflowFormData>(
      section: K,
      data: AccountWorkflowFormData[K],
    ) => {
      setFormData(prev =>
        produce(prev, draft => {
          draft[section] = data;
        }),
      );
    },
    [],
  );

  const getFormData = useCallback(
    <K extends keyof AccountWorkflowFormData>(
      section: K,
    ): AccountWorkflowFormData[K] => {
      return formData[section];
    },
    [formData],
  );

  const getAllFormData = useCallback(() => {
    return formData;
  }, [formData]);

  const [Modal, modal] = useModal({
    component: 'dialog',
    defaultOpen: false,
    beforeOpen: () => {
      reset();
      skipProfileHydrationRef.current = false;
      skipOnboardingHydrationRef.current = false;

      if (workflow === 'combined' && hasExistingInversisAccount) {
        setBlocks(getCombinedProfilingBlocks());
      } else {
        setBlocks(getInitialBlocks(workflow));
      }

      setNavigationFloorStepId(undefined);
      setVerificationCompleted(false);
    },
  });

  const savePersonalData = useCallback(
    async (
      data: AccountWorkflowFormData['personalData'],
      options?: SavePersonalDataOptions,
    ) => {
      saveFormData('personalData', data);
      skipProfileHydrationRef.current = true;

      if (options?.updateRemoteProfile === false) {
        return;
      }

      return updateCitecUserClientProfile(getClientProfilePayload(data)).unwrap();
    },
    [saveFormData, updateCitecUserClientProfile],
  );

  const saveAccountOpeningData = useCallback(
    async (options?: SaveAccountOpeningDataOptions) => {
      skipOnboardingHydrationRef.current = true;

      const data = options?.updatedFormData
        ? { ...formData, ...options.updatedFormData }
        : formData;

      const payload = buildAccountOpeningPayload(data, {
        submitToInversis: options?.submitToInversis ?? false,
      });

      return updateClientOnboardingProfile(payload).unwrap();
    },
    [formData, updateClientOnboardingProfile],
  );

  const saveAdvisoryContractData = useCallback(
    async (
      updatedData?: Partial<
        Omit<AccountWorkflowFormData, 'personalData' | 'resultAndSignature'>
      >,
      options?: SaveAdvisoryContractDataOptions,
    ) => {
      if (!proposal) {
        throw new Error('Profiling workflow requires a proposal.');
      }

      const data = updatedData ? { ...formData, ...updatedData } : formData;

      return updateAdvisoryContract({
        calculate_test_results: options?.calculateTestResults ?? false,
        advisory_proposal_id: proposal.id,
        onboarding_qa: buildOnboardingQA(data, t),
      }).unwrap();
    },
    [formData, proposal, t, updateAdvisoryContract],
  );

  const signAdvisoryContract = useCallback(async () => {
    if (!proposal) {
      throw new Error('Profiling workflow requires a proposal.');
    }

    return signAdvisoryContractMutation({ proposal_id: proposal.id }).unwrap();
  }, [proposal, signAdvisoryContractMutation]);

  useEffect(() => {
    if (
      skipProfileHydrationRef.current ||
      !profileData ||
      !clientProfileQuery.isSuccess
    ) {
      return;
    }

    setFormData(prev =>
      produce(prev, draft => {
        hydratePersonalDataFromCitecClientProfile(draft.personalData, profileData);
      }),
    );
  }, [profileData, clientProfileQuery.isSuccess]);

  const shouldHydrateOnboardingState =
    workflow === 'account-opening' ||
    (workflow === 'combined' && !hasExistingInversisAccount);

  useEffect(() => {
    if (
      !shouldHydrateOnboardingState ||
      skipOnboardingHydrationRef.current ||
      !onboardingState ||
      !onboardingStateQuery.isSuccess ||
      !hasOnboardingSavedData(onboardingState)
    ) {
      return;
    }

    const accountOpeningFormData = buildAccountOpeningFormData(onboardingState);

    setFormData(prev => ({
      ...prev,
      ...accountOpeningFormData,
      signature: prev.signature,
    }));
  }, [
    onboardingState,
    onboardingStateQuery.isSuccess,
    shouldHydrateOnboardingState,
  ]);

  useEffect(() => {
    if (workflow !== 'account-opening' && proposal?.contract !== null) {
      setFormData(currentFormData => ({
        ...currentFormData,
        ...buildOnboardingFormData(proposal.contract!.onboarding_qa),
      }));
    }
  }, [proposal?.contract, proposal?.contract?.onboarding_qa, workflow]);

  return {
    workflow,
    hasExistingInversisAccount,
    hasCompletedProfiling,
    hasPartialProfilingData,
    canResumeToProfilingResults,
    isAwaitingContractSignature,
    navigationFloorStepId,
    setNavigationFloor,
    Modal,
    modal,
    blocks,
    formData,
    saveFormData,
    getFormData,
    getAllFormData,
    markStepAsCompleted,
    isStepCompleted,
    skipAccountOpeningPhase,
    switchToProfilingBlocks,
    resumeToProfilingResults,
    isCitecProfileLoading: clientProfileQuery.isLoading,
    isUpdatingClientOnboardingProfile,
    profileData,
    savePersonalData,
    saveAccountOpeningData,
    isUpdatingCitecUserClientProfile,
    verificationCompleted,
    setVerificationCompleted,
    proposal,
    saveAdvisoryContractData,
    isUpdatingAdvisoryContract,
    suitabilityTestResults,
    isSigningAdvisoryContract,
    signAdvisoryContract,
  } as const;
};

export const {
  useAccountWorkflowData,
  AccountWorkflowProvider,
  AccountWorkflowDataContext,
  withAccountWorkflow,
} = createDataProvider({
  name: 'AccountWorkflow',
  factory: useAccountWorkflowFactory,
});
